import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import type { FilingObligationDto } from '@signalvote/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { triggerBrowserDownload } from '../reports/downloadHelpers';
import { PreSessionReportModal } from '../reports/PreSessionReportModal';
import { formatDateTime } from '../utils';
import { noticeLabel } from './noticeCountdown';
import {
  useGenerateObligationReport,
  useMarkObligationReported,
  useMarkDailyReportFiled,
} from '@/hooks/queries/useFinanceReports';
import {
  dailyMarkFiledConfirmCopy,
  generateRapidConfirmCopy,
  markRapidReportedConfirmCopy,
  markPreSessionReportedConfirmCopy,
  generateNoticeConfirmCopy,
  markNoticeReportedConfirmCopy,
  type QueueConfirmCopy,
} from './actionQueueConfirmCopy';

/**
 * The action queue's row actions — each owns its mutation, extracted from
 * ActionQueue.tsx so the queue shell stays presentational. The `inert` prop is
 * the collapsed-deck contract: the button drops out of the tab order + hides
 * from AT so the whole stack is the single tab stop.
 *
 * Every action confirms through `QueueConfirmDialog` before its mutation fires
 * (the ADR-023 treatment `MarkFiledInPortalButton` established) — the queue
 * actions predated ADR-023 and used to fire instantly.
 */

/**
 * The one-screen confirm the queue actions share: title, the transaction under
 * confirmation as a ledger-like LINE ITEM (primary / amount / meta — not
 * prose, so the treasurer verifies the row at a glance), the consequence
 * sentence, and a committing button labeled with the consequence; cancel =
 * "Go back". The Cancel wires `onOpenChange(false)` explicitly so the close
 * contract holds even with the primitive mocked to plain elements in tests
 * (Radix also closes it natively).
 */
export function QueueConfirmDialog({
  open,
  onOpenChange,
  copy,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  copy: QueueConfirmCopy;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <div
            data-testid="action-queue-confirm-item"
            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 text-sm font-semibold text-slate-900">
                {copy.item.primary}
              </span>
              {copy.item.amount && (
                <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-900">
                  {copy.item.amount}
                </span>
              )}
            </div>
            {copy.item.meta && (
              <div className="mt-0.5 text-xs text-slate-500">{copy.item.meta}</div>
            )}
          </div>
          <AlertDialogDescription data-testid="action-queue-confirm-consequence">
            {copy.consequence}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="action-queue-confirm-cancel"
            onClick={() => onOpenChange(false)}
          >
            Go back
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="action-queue-confirm-accept"
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
          >
            {copy.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RapidAction({
  obligation,
  onResolved,
  inert,
}: {
  obligation: FilingObligationDto;
  onResolved: () => void;
  /** Collapsed-deck front card: the button drops out of the tab order + hides
   *  from AT so the stack is the single tab stop. */
  inert: boolean;
}) {
  // Golden Rule — destructure the reference-stable mutate function.
  const { generateObligationReport } = useGenerateObligationReport();
  const { markObligationReported } = useMarkObligationReported();
  const model = usePortalUploadModel();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Server-state-driven two-state control: ungenerated → "Generate now"; once
  // GeneratedAt is stamped → "Mark as reported". Marking reported retires the
  // obligation, so it resolves out of the queue.
  const hasGenerated = obligation.generatedAt != null;

  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await generateObligationReport(obligation.id);
      triggerBrowserDownload(result.fileBlob, result.fileName);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate the rapid-report file.');
    } finally {
      setBusy(false);
    }
  };

  const handleMarkReported = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await markObligationReported(obligation.id);
      onResolved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not mark this rapid report as reported.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setError(null);
            setConfirmOpen(true);
          }}
          disabled={busy}
          aria-hidden={inert || undefined}
          tabIndex={inert ? -1 : undefined}
          data-testid={
            hasGenerated
              ? `action-queue-mark-reported-${obligation.id}`
              : `action-queue-generate-${obligation.id}`
          }
          className="rounded bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-200 disabled:opacity-60"
        >
          {hasGenerated ? 'Mark as reported' : 'Generate now'}
        </button>
        {/* Quiet retrieval, deliberately subordinate to the committing action:
            re-runs the same generate endpoint (builders are pure — unchanged
            data returns the identical file, corrected data a corrected one) so
            a lost or stale download never strands the treasurer. No confirm:
            it commits nothing and the first-generate dialog already taught
            generate ≠ file. */}
        {hasGenerated && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              void handleGenerate();
            }}
            disabled={busy}
            aria-hidden={inert || undefined}
            tabIndex={inert ? -1 : undefined}
            data-testid={`action-queue-download-${obligation.id}`}
            className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Download file
          </button>
        )}
      </div>
      {/* The card explains its own state: this line is WHY the button reads
          "Mark as reported" instead of "Generate now". */}
      {hasGenerated && obligation.generatedAt && (
        <p
          data-testid={`action-queue-generated-at-${obligation.id}`}
          className="text-[11px] text-slate-400"
        >
          File generated {formatDateTime(obligation.generatedAt)}
        </p>
      )}
      {/* One dialog, two-state copy mirroring the button: pre-generate it
          explains generate ≠ file (ADR-019); post-generate it carries the
          committing mark-reported trust contract. */}
      <QueueConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        copy={
          hasGenerated
            ? markRapidReportedConfirmCopy(obligation, model)
            : generateRapidConfirmCopy(obligation, model)
        }
        onConfirm={() => void (hasGenerated ? handleMarkReported() : handleGenerate())}
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The VA §24.2-947.11 pre-session report's queue action (GitHub #164, ADR-039) —
 * a two-state control mirroring `RapidAction`, but the heavy step lives in a
 * modal, not on the card:
 *   • before GeneratedAt → a single "Review & file" button that opens
 *     `PreSessionReportModal` (confirm the session date, review what will file,
 *     generate the COMET XML). The card does NOT fetch the preview.
 *   • after GeneratedAt → an inline "Mark as reported" confirm, reusing the
 *     shared ADR-023 dialog with SUPPLEMENTAL copy (the gifts stay on the next
 *     periodic report — marking reported removes nothing). Generate happens only
 *     in the modal; mark-reported only here.
 */
export function PreSessionAction({
  obligation,
  onResolved,
  inert,
}: {
  obligation: FilingObligationDto;
  onResolved: () => void;
  /** Collapsed-deck front card: the button drops out of the tab order + hides
   *  from AT so the stack is the single tab stop. */
  inert: boolean;
}) {
  // Golden Rule — destructure the reference-stable mutate function.
  const { markObligationReported } = useMarkObligationReported();
  const model = usePortalUploadModel();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Server-state-driven two-state control (persists across sessions via
  // GeneratedAt): ungenerated → "Review & file" opens the modal; generated →
  // inline "Mark as reported".
  const hasGenerated = obligation.generatedAt != null;

  const handleMarkReported = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await markObligationReported(obligation.id);
      onResolved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not mark this pre-session report as reported.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setError(null);
            if (hasGenerated) setConfirmOpen(true);
            else setModalOpen(true);
          }}
          disabled={busy}
          aria-hidden={inert || undefined}
          tabIndex={inert ? -1 : undefined}
          data-testid={
            hasGenerated
              ? `action-queue-mark-reported-${obligation.id}`
              : `action-queue-review-${obligation.id}`
          }
          className="rounded bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-200 disabled:opacity-60"
        >
          {hasGenerated ? 'Mark as reported' : 'Review & file'}
        </button>
      </div>
      {/* Explains its own state: this line is WHY the button reads "Mark as
          reported" — the file's already generated (in the modal). */}
      {hasGenerated && obligation.generatedAt && (
        <p
          data-testid={`action-queue-generated-at-${obligation.id}`}
          className="text-[11px] text-slate-400"
        >
          File generated {formatDateTime(obligation.generatedAt)}
        </p>
      )}
      {/* Mark-reported confirm — SUPPLEMENTAL trust contract (ADR-039): the gifts
          still file on the next periodic report; marking reported removes nothing. */}
      <QueueConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        copy={markPreSessionReportedConfirmCopy(obligation, model)}
        onConfirm={() => void handleMarkReported()}
      />
      {/* The rich review flow. Conditional-mount (like FilePreflightModal's
          AttestationModal) keeps its query subscriptions out of the collapsed
          front card's render path. */}
      {modalOpen && (
        <PreSessionReportModal
          open={modalOpen}
          obligation={obligation}
          onClose={() => setModalOpen(false)}
        />
      )}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A 48-hour notice's queue action (ADR-038) — shared by NC §163-278.9(a)(2) (GitHub #301) and the
 * federal 11 CFR 104.5(f) Form 6 notice (#708). Both are SUPPLEMENTAL: the obligation flips Filed, the
 * gift is NOT flipped — it stays on the periodic report that must also disclose it.
 *
 * They differ in one place: whether the app can build the artifact. The server says so with
 * `canGenerate` — never a jurisdiction string here.
 *   • `canGenerate` → the two-state Generate → Mark-as-reported control `RapidAction` uses, because
 *     the federal Form 6 emitter exists.
 *   • otherwise → the single "Mark as reported". NC's `.cfd` builder is a deferred sibling ticket
 *     (`GenerateAsync` refuses), so offering "Generate now" would strand the treasurer at a dead end;
 *     they file in the NCSBE CFRS portal, then mark it reported here. When that builder lands, NC
 *     grows the generate step by flipping the server flag — no change here.
 */
export function NoticeAction({
  obligation,
  onResolved,
  inert,
}: {
  obligation: FilingObligationDto;
  onResolved: () => void;
  /** Collapsed-deck front card: the button drops out of the tab order + hides
   *  from AT so the stack is the single tab stop. */
  inert: boolean;
}) {
  // Golden Rule — destructure the reference-stable mutate function.
  const { generateObligationReport } = useGenerateObligationReport();
  const { markObligationReported } = useMarkObligationReported();
  const model = usePortalUploadModel();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Server-state-driven, exactly like RapidAction: a notice the app can build starts at "Generate
  // now" and flips to "Mark as reported" once GeneratedAt is stamped, so the state survives a reload.
  // A notice the app cannot build has only the one step.
  const hasGenerated = obligation.generatedAt != null;
  const showGenerate = obligation.canGenerate && !hasGenerated;

  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await generateObligationReport(obligation.id);
      triggerBrowserDownload(result.fileBlob, result.fileName);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not generate the ${noticeLabel(obligation)} file.`);
    } finally {
      setBusy(false);
    }
  };

  const handleMarkReported = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await markObligationReported(obligation.id);
      onResolved();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not mark this ${noticeLabel(obligation)} as reported.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setError(null);
            setConfirmOpen(true);
          }}
          disabled={busy}
          aria-hidden={inert || undefined}
          tabIndex={inert ? -1 : undefined}
          data-testid={
            showGenerate
              ? `action-queue-generate-${obligation.id}`
              : `action-queue-mark-reported-${obligation.id}`
          }
          className="rounded bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-200 disabled:opacity-60"
        >
          {showGenerate ? 'Generate now' : 'Mark as reported'}
        </button>
        {/* Quiet re-download, subordinate to the committing action — the same affordance RapidAction
            offers so a lost file never strands the treasurer. Builders are pure: unchanged data
            returns the identical file. */}
        {obligation.portalRun && (
          <Link
            to="/admin/finance/reports/$reportId"
            params={{ reportId: obligation.portalRun.reportId }}
            search={{ notice: obligation.portalRun.noticeOf }}
            aria-hidden={inert || undefined}
            tabIndex={inert ? -1 : undefined}
            data-testid={`action-queue-portal-run-${obligation.id}`}
            className="rounded px-2 py-1.5 text-xs font-medium text-slate-600 underline hover:text-slate-900"
          >
            Upload it to {model.portalName}
          </Link>
        )}
        {obligation.canGenerate && hasGenerated && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              void handleGenerate();
            }}
            disabled={busy}
            aria-hidden={inert || undefined}
            tabIndex={inert ? -1 : undefined}
            data-testid={`action-queue-download-${obligation.id}`}
            className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Download file
          </button>
        )}
      </div>
      {/* The card explains its own state: why the button reads "Mark as reported". */}
      {obligation.canGenerate && obligation.generatedAt && (
        <p
          data-testid={`action-queue-generated-at-${obligation.id}`}
          className="text-[11px] text-slate-400"
        >
          File generated {formatDateTime(obligation.generatedAt)}
        </p>
      )}
      {/* Two-state copy mirroring the button: pre-generate it restates generate ≠ file (ADR-019);
          otherwise the supplemental trust contract (ADR-038) — the gift still files on the next
          periodic report, so marking the notice reported removes nothing. */}
      <QueueConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        copy={
          showGenerate
            ? generateNoticeConfirmCopy(obligation, model)
            : markNoticeReportedConfirmCopy(obligation, model)
        }
        onConfirm={() => void (showGenerate ? handleGenerate() : handleMarkReported())}
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

export function DailyAction({
  obligation,
  onResolved,
  inert,
}: {
  obligation: FilingObligationDto;
  onResolved: () => void;
  /** Collapsed-deck front card: the button drops out of the tab order + hides
   *  from AT so the stack is the single tab stop. */
  inert: boolean;
}) {
  // Golden Rule — destructure the reference-stable mutate function.
  const { markDailyReportFiled } = useMarkDailyReportFiled();
  const model = usePortalUploadModel();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleMarkFiled = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await markDailyReportFiled(obligation.id);
      onResolved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not mark this daily report filed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <button
        type="button"
        onClick={() => {
          setError(null);
          setConfirmOpen(true);
        }}
        disabled={busy}
        aria-hidden={inert || undefined}
        tabIndex={inert ? -1 : undefined}
        data-testid={`action-queue-mark-filed-${obligation.id}`}
        className="self-start rounded bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-200 disabled:opacity-60"
      >
        Mark filed in portal
      </button>
      <QueueConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        copy={dailyMarkFiledConfirmCopy(obligation, model)}
        onConfirm={() => void handleMarkFiled()}
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
